import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Table, Badge, Alert, Spinner } from 'react-bootstrap';
import PicklistApi from '../../api/PicklistApi';
import './css/PicklistValueManager.css';

function normalizeHexColor(s) {
  if (s == null || typeof s !== 'string') return null;
  const t = s.trim();
  if (!/^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test(t)) return null;
  if (t.length === 4) {
    return `#${t[1]}${t[1]}${t[2]}${t[2]}${t[3]}${t[3]}`.toLowerCase();
  }
  return t.toLowerCase();
}

/** @param {string} line @param {string|null} defaultPathColor normalized hex or null */
function parseBulkPicklistLine(line, defaultPathColor) {
  const raw = line.trim();
  if (!raw) return null;
  let pathColor = defaultPathColor && normalizeHexColor(defaultPathColor);
  let rest = raw;
  const m = raw.match(/^(.+?)\s+#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/);
  if (m) {
    rest = m[1].trim();
    pathColor = normalizeHexColor(`#${m[2]}`) || pathColor;
  }
  if (rest.includes('|')) {
    const parts = rest.split('|');
    const valueKey = (parts[0] || '').trim();
    const valueLabel = (parts.slice(1).join('|') || '').trim() || valueKey;
    if (!valueKey) return null;
    return { valueKey, valueLabel, pathColor: pathColor || null };
  }
  return { valueKey: rest, valueLabel: rest, pathColor: pathColor || null };
}

const PicklistValueManager = ({
  show,
  onHide,
  field,
  onValuesUpdated,
  objectName,
  /**
   * When false (standard picklist without full standard field edit): new values cannot be added; labels, path colors,
   * default, order, deactivate, delete, and reactivate are allowed. API value keys are always read-only after creation
   * (for all modes).
   */
  allowValueKeyEdit = true,
}) => {
  const isLabelOnlyMode = allowValueKeyEdit === false;
  const [values, setValues] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const [editingValue, setEditingValue] = useState(null);
  const [usageCounts, setUsageCounts] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [valueToDelete, setValueToDelete] = useState(null);
  const [activeValuesForReplacement, setActiveValuesForReplacement] = useState([]);
  const [selectedReplacementValue, setSelectedReplacementValue] = useState('');
  const [multipleValues, setMultipleValues] = useState('');
  const [bulkPathColor, setBulkPathColor] = useState('');

  const picklistApi = new PicklistApi();

  useEffect(() => {
    if (show && field) {
      loadPicklistValues();
    }
  }, [show, field]);

  const loadPicklistValues = async () => {
    if (!field) return;

    if (!field.id) {
      setError(
        'This field has no ID yet. Save the field on the object first, then open Manage Values again.'
      );
      setValues([]);
      return;
    }

    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.getPicklistValues(field.id);
      if (response.data.success) {
        setValues(response.data.data || []);
        // Load usage counts for each value
        await loadUsageCounts(response.data.data || []);
      } else {
        setError('Failed to load picklist values');
      }
    } catch (error) {
      console.error('Error loading picklist values:', error);
      setError('Failed to load picklist values: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const loadUsageCounts = async (picklistValues) => {
    const counts = {};
    /*for (const value of picklistValues) {
        try {
            const response = await picklistApi.getPicklistValueUsage(value.id);
            if (response.data.success) {
                counts[value.id] = response.data.data.count;
            }
        } catch (error) {
            console.error('Error loading usage count for value:', value.id, error);
            counts[value.id] = 0;
        }
    }*/
    setUsageCounts(counts);
  };



  const handleUpdateValue = async () => {
    if (!editingValue || !editingValue.valueLabel.trim()) {
      setError('Value label is required');
      return;
    }
    setLoading(true);
    setError('');

    try {
      const pathColorPayload = (editingValue.pathColor || '').trim() || null;
      const payload = {
        valueLabel: editingValue.valueLabel.trim(),
        isDefault: editingValue.isDefault,
        sortOrder: editingValue.sortOrder,
        pathColor: pathColorPayload,
      };
      const response = await picklistApi.updatePicklistValue(editingValue.id, payload);

      if (response.data.success) {
        setEditingValue(null);
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to update picklist value');
      }
    } catch (error) {
      console.error('Error updating picklist value:', error);
      setError('Failed to update picklist value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeactivateValue = async (value) => {
    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.deactivatePicklistValue(value.id, null);
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to deactivate picklist value');
      }
    } catch (error) {
      console.error('Error deactivating picklist value:', error);
      setError('Failed to deactivate picklist value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleReactivateValue = async (value) => {
    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.reactivatePicklistValue(value.id);
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to reactivate picklist value');
      }
    } catch (error) {
      console.error('Error reactivating picklist value:', error);
      setError('Failed to reactivate picklist value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleSetAsDefault = async (value) => {
    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.updatePicklistValue(value.id, {
        isDefault: true
      });
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to set default value');
      }
    } catch (error) {
      console.error('Error setting default value:', error);
      setError('Failed to set default value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveDefault = async (value) => {
    setLoading(true);
    setError('');

    try {
      const response = await picklistApi.updatePicklistValue(value.id, {
        isDefault: false
      });
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to remove default value');
      }
    } catch (error) {
      console.error('Error removing default value:', error);
      setError('Failed to remove default value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleAddMultipleValues = async () => {
    if (!field?.id) {
      setError('Cannot add values until this field is saved (missing field ID).');
      return;
    }
    if (!multipleValues.trim()) {
      setError('Please enter values to add');
      return;
    }

    const lines = multipleValues.split('\n').map(line => line.trim()).filter(line => line.length > 0);
    if (lines.length === 0) {
      setError('Please enter at least one value');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const defaultHex = normalizeHexColor(bulkPathColor);
      for (let i = 0; i < lines.length; i++) {
        const parsed = parseBulkPicklistLine(lines[i], defaultHex);
        if (!parsed) {
          continue;
        }
        const payload = {
          fieldId: field.id,
          valueKey: parsed.valueKey,
          valueLabel: parsed.valueLabel,
          isDefault: false,
          sortOrder: values.length + i
        };
        if (parsed.pathColor) {
          payload.pathColor = parsed.pathColor;
        }
        const response = await picklistApi.addPicklistValue(payload);

        if (!response.data.success) {
          throw new Error(`Failed to add value: ${lines[i]}`);
        }
      }

      setMultipleValues('');
      setBulkPathColor('');
      await loadPicklistValues();
      // Don't call onValuesUpdated() here to prevent modal from closing
    } catch (error) {
      console.error('Error adding multiple picklist values:', error);
      setError('Failed to add multiple picklist values: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteValue = async (value) => {
    // Get active values for replacement selection
    const activeValues = values.filter(v => v.is_active && v.id !== value.id);

    if (activeValues.length === 0) {
      setError('No active values available for replacement. Please activate another value first.');
      return;
    }

    setValueToDelete(value);
    setActiveValuesForReplacement(activeValues);
    setShowDeleteConfirm(true);
  };

  const confirmDeleteValue = async () => {
    if (!valueToDelete) return;

    if (!selectedReplacementValue) {
      setError('Choose a replacement value so existing records can be updated before this option is removed.');
      return;
    }

    if (!field?.id) {
      setError('Field ID is missing; save the field and try again.');
      return;
    }

    setLoading(true);
    setError('');

    try {
      const replaceResp = await picklistApi.replacePicklistValues(
        field.id,
        valueToDelete.value_key,
        selectedReplacementValue
      );
      if (!replaceResp.data?.success) {
        throw new Error(replaceResp.data?.message || 'Failed to update records with replacement value');
      }

      const response = await picklistApi.deletePicklistValue(valueToDelete.id);
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to delete picklist value');
      }
    } catch (error) {
      console.error('Error deleting picklist value:', error);
      setError('Failed to delete picklist value: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
      setShowDeleteConfirm(false);
      setValueToDelete(null);
      setSelectedReplacementValue('');
      setActiveValuesForReplacement([]);
    }
  };

  const handleReorderValues = async (values) => {
    setLoading(true);
    setError('');

    try {
      const updatedValues = values.map((value, index) => ({
        id: value.id,
        sortOrder: index
      }));

      const response = await picklistApi.bulkUpdatePicklistValues(field.id, updatedValues);
      if (response.data.success) {
        await loadPicklistValues();
        // Don't call onValuesUpdated() here to prevent modal from closing
      } else {
        setError('Failed to reorder picklist values');
      }
    } catch (error) {
      console.error('Error reordering picklist values:', error);
      setError('Failed to reorder picklist values: ' + (error.response?.data?.message || error.message));
    } finally {
      setLoading(false);
    }
  };

  const moveValue = (index, direction) => {
    const newValues = [...values];
    const newIndex = direction === 'up' ? index - 1 : index + 1;

    if (newIndex >= 0 && newIndex < newValues.length) {
      [newValues[index], newValues[newIndex]] = [newValues[newIndex], newValues[index]];
      setValues(newValues);
      handleReorderValues(newValues);
    }
  };

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" className="picklist-manager-modal picklist-value-manager">
        <Modal.Header closeButton>
          <Modal.Title className="d-flex flex-wrap align-items-baseline gap-2">
            <span>Manage Picklist Values</span>
            {objectName ? (
              <span className="badge bg-secondary align-middle fw-normal">Object: {objectName}</span>
            ) : null}
            <span className="text-muted fs-6 fw-normal">
              {field?.field_label || field?.field_name || 'Unknown'}
              {field?.field_name ? (
                <code className="ms-1 small">{field.field_name}</code>
              ) : null}
            </span>
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError('')}>
              {error}
            </Alert>
          )}

          {isLabelOnlyMode && (
            <Alert variant="info" className="mb-3">
              <strong>Limited standard picklist edit:</strong> You can change display labels, path colors, default,
              and order. You can <strong>deactivate</strong>, <strong>delete</strong>, or <strong>reactivate</strong>{' '}
              values. <strong>New values</strong> cannot be added without full standard field edit in your license.
            </Alert>
          )}

          {!isLabelOnlyMode && (
            <Alert variant="secondary" className="mb-3 py-2">
              <strong>API value key</strong> is set when you add a value and <strong>cannot be edited</strong>. To use a
              different key, delete the value and add it again.
            </Alert>
          )}

          {/* Add Values Form */}
          {!isLabelOnlyMode && (
          <div className="add-multiple-values-section mb-4">
            <h6>Add Values</h6>
            <Form>
              <div className="row">
                <div className="col-md-10">
                  <Form.Group>
                    <Form.Label>Values (one per line)</Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={4}
                      value={multipleValues}
                      onChange={(e) => setMultipleValues(e.target.value)}
                      placeholder={'One value per line. Examples:\nOpen\nqualified|Qualified\nClosed #2e7d32'}
                    />
                    <Form.Text className="text-muted">
                      Same label and key unless you use <code>api_name|Label</code> (API name is permanent after save).
                      Optional color: end a line with <code>#RRGGBB</code>, or set default path color below for all new lines.
                    </Form.Text>
                  </Form.Group>
                  <div className="d-flex flex-wrap align-items-center gap-2 mt-2">
                    <Form.Label className="mb-0 small text-muted">Default path color (new lines)</Form.Label>
                    <Form.Control
                      type="color"
                      value={
                        /^#([0-9A-Fa-f]{6})$/.test((bulkPathColor || '').trim())
                          ? bulkPathColor.trim().toLowerCase()
                          : '#6c757d'
                      }
                      onChange={(e) => setBulkPathColor(e.target.value)}
                      title="Applied to each added line unless the line ends with its own #hex"
                      style={{ width: '42px', height: '32px', padding: '2px' }}
                    />
                    <Form.Control
                      type="text"
                      size="sm"
                      placeholder="#RRGGBB"
                      value={bulkPathColor}
                      onChange={(e) => setBulkPathColor(e.target.value)}
                      style={{ width: '100px' }}
                    />
                    <Button type="button" size="sm" variant="outline-secondary" onClick={() => setBulkPathColor('')}>
                      Clear
                    </Button>
                  </div>
                </div>
                <div className="col-md-2">
                  <Form.Label>&nbsp;</Form.Label>
                  <Button
                    variant="primary"
                    onClick={handleAddMultipleValues}
                    disabled={loading || !multipleValues.trim() || !field?.id}
                    className="w-100"
                  >
                    {loading ? <Spinner animation="border" size="sm" /> : 'Add All'}
                  </Button>
                </div>
              </div>
            </Form>
          </div>
          )}

          {/* Active Values Section */}
          <div className="values-section mb-4">
            <h6>Active Values ({values.filter(v => v.is_active).length})</h6>
            {loading ? (
              <div className="text-center py-4">
                <Spinner animation="border" />
                <p className="mt-2">Loading picklist values...</p>
              </div>
            ) : values.filter(v => v.is_active).length === 0 ? (
              <Alert variant="info">
                {isLabelOnlyMode
                  ? 'No active values found. Reactivate a value from the inactive list if needed.'
                  : 'No active values found. Add some values above or reactivate inactive ones.'}
              </Alert>
            ) : (
              <Table responsive className="picklist-values-table">
                <thead>
                  <tr>
                    <th>Order</th>
                    <th>Value Label</th>
                    <th>Value Key (locked)</th>
                    <th>Default</th>
                    <th>Path color</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {values.filter(v => v.is_active).map((value, index) => {
                    const originalIndex = values.findIndex(v => v.id === value.id);
                    return (
                      <tr key={value.id}>
                        <td>
                          <div className="order-controls">
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => moveValue(originalIndex, 'up')}
                              disabled={originalIndex === 0}
                            >
                              ↑
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-secondary"
                              onClick={() => moveValue(originalIndex, 'down')}
                              disabled={originalIndex === values.length - 1}
                            >
                              ↓
                            </Button>
                          </div>
                        </td>
                        <td>
                          {editingValue?.id === value.id ? (
                            <Form.Control
                              type="text"
                              value={editingValue.valueLabel}
                              onChange={(e) => setEditingValue({ ...editingValue, valueLabel: e.target.value })}
                            />
                          ) : (
                            <span>{value.value_label}</span>
                          )}
                        </td>
                        <td>
                          {editingValue?.id === value.id ? (
                            <code className="text-muted">{editingValue.valueKey}</code>
                          ) : (
                            <code>{value.value_key}</code>
                          )}
                        </td>
                        <td>
                          {editingValue?.id === value.id ? (
                            <Form.Check
                              type="checkbox"
                              checked={editingValue.isDefault}
                              onChange={(e) => setEditingValue({ ...editingValue, isDefault: e.target.checked })}
                            />
                          ) : (
                            <Badge bg={value.is_default ? 'primary' : 'light'} text={value.is_default ? 'white' : 'dark'}>
                              {value.is_default ? 'Default' : 'No'}
                            </Badge>
                          )}
                        </td>
                        <td>
                          {editingValue?.id === value.id ? (
                            <div className="d-flex align-items-center gap-2 flex-wrap">
                              <Form.Control
                                type="color"
                                value={
                                  /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/.test((editingValue.pathColor || '').trim())
                                    ? (editingValue.pathColor || '').trim().length === 4
                                      ? `#${(editingValue.pathColor || '')[1]}${(editingValue.pathColor || '')[1]}${(editingValue.pathColor || '')[2]}${(editingValue.pathColor || '')[2]}${(editingValue.pathColor || '')[3]}${(editingValue.pathColor || '')[3]}`
                                      : (editingValue.pathColor || '').trim()
                                    : '#6c757d'
                                }
                                onChange={(e) => setEditingValue({ ...editingValue, pathColor: e.target.value })}
                                title="Pick a color for the record path"
                                style={{ width: '42px', height: '32px', padding: '2px' }}
                              />
                              <Form.Control
                                type="text"
                                size="sm"
                                placeholder="#RRGGBB"
                                value={editingValue.pathColor || ''}
                                onChange={(e) => setEditingValue({ ...editingValue, pathColor: e.target.value })}
                                style={{ width: '100px' }}
                              />
                              <Button
                                size="sm"
                                variant="outline-secondary"
                                onClick={() => setEditingValue({ ...editingValue, pathColor: '' })}
                              >
                                Clear
                              </Button>
                            </div>
                          ) : (
                            <span className="d-flex align-items-center gap-2">
                              {value.path_color ? (
                                <>
                                  <span
                                    className="picklist-path-color-swatch"
                                    style={{
                                      display: 'inline-block',
                                      width: '18px',
                                      height: '18px',
                                      borderRadius: '3px',
                                      border: '1px solid #ccc',
                                      background: value.path_color
                                    }}
                                  />
                                  <code className="small">{value.path_color}</code>
                                </>
                              ) : (
                                <span className="text-muted small">Default</span>
                              )}
                            </span>
                          )}
                        </td>
                        <td>
                          {editingValue?.id === value.id ? (
                            <div className="action-buttons">
                              <Button size="sm" variant="success" onClick={handleUpdateValue}>
                                Save
                              </Button>
                              <Button size="sm" variant="secondary" onClick={() => setEditingValue(null)}>
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <div className="action-buttons">
                              <Button size="sm" variant="primary" onClick={() => setEditingValue({
                                id: value.id,
                                valueKey: value.value_key,
                                valueLabel: value.value_label,
                                isDefault: value.is_default,
                                sortOrder: value.sort_order,
                                pathColor: value.path_color || ''
                              })}>
                                Edit
                              </Button>
                              {value.is_default ? (
                                <Button size="sm" variant="outline-warning" onClick={() => handleRemoveDefault(value)}>
                                  Remove Default
                                </Button>
                              ) : (
                                <Button size="sm" variant="info" onClick={() => handleSetAsDefault(value)}>
                                  Set as Default
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="warning"
                                onClick={() => handleDeactivateValue(value)}
                              >
                                Deactivate
                              </Button>
                              <Button
                                size="sm"
                                variant="danger"
                                onClick={() => handleDeleteValue(value)}
                              >
                                Delete
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </Table>
            )}
          </div>

          {/* Inactive Values Section */}
          <div className="values-section">
            <h6>Inactive Values ({values.filter(v => !v.is_active).length})</h6>
            {values.filter(v => !v.is_active).length === 0 ? (
              <Alert variant="info">No inactive values found.</Alert>
            ) : (
              <Table responsive className="picklist-values-table">
                <thead>
                  <tr>
                    <th>Value Label</th>
                    <th>Value Key (locked)</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {values.filter(v => !v.is_active).map((value) => (
                    <tr key={value.id} className="inactive-value">
                      <td>
                        <span>{value.value_label}</span>
                      </td>
                      <td>
                        <code>{value.value_key}</code>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <Button
                            size="sm"
                            variant="success"
                            onClick={() => handleReactivateValue(value)}
                          >
                            Reactivate
                          </Button>
                          <Button
                            size="sm"
                            variant="danger"
                            onClick={() => handleDeleteValue(value)}
                          >
                            Delete
                          </Button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            )}
          </div>
        </Modal.Body>

        <Modal.Footer>
          <Button variant="secondary" onClick={onHide}>
            Close
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Delete Confirmation Modal */}
      <Modal show={showDeleteConfirm} onHide={() => setShowDeleteConfirm(false)}>
        <Modal.Header closeButton>
          <Modal.Title>Confirm Delete</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p>Are you sure you want to delete "{valueToDelete?.value_label}"? This action cannot be undone.</p>

          <div className="mt-3">
            <Form.Label>Replace existing records with:</Form.Label>
            <Form.Select
              value={selectedReplacementValue}
              onChange={(e) => setSelectedReplacementValue(e.target.value)}
            >
              <option value="">-- Select replacement value (required) --</option>
              {activeValuesForReplacement.map((value) => (
                <option key={value.id} value={value.value_key}>
                  {value.value_label} ({value.value_key})
                </option>
              ))}
            </Form.Select>
            <Form.Text className="text-muted">
              Records are updated in the background on the server (batched), then this picklist option is deleted.
            </Form.Text>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => {
            setShowDeleteConfirm(false);
            setSelectedReplacementValue('');
            setActiveValuesForReplacement([]);
          }}>
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={confirmDeleteValue}
            disabled={loading || !selectedReplacementValue}
          >
            {loading ? <Spinner animation="border" size="sm" /> : 'Delete'}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PicklistValueManager;
